import type { PostFunction } from '@servicetim/shared';
import { useCannedResponses } from '@servicetim/ui/api';
import {
  AsyncPicker,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Textarea,
  useUserOptionLoader,
} from '@servicetim/ui/components';
import { Plus, Trash2 } from 'lucide-react';
import {
  type CatalogField,
  type FieldCatalog,
  SETTABLE_SYSTEM_FIELDS,
} from '../../projects/catalog/field-catalog';
import { fieldOptions } from '../../projects/conditions/field-condition-row';
import { ConditionValueControl } from '../../projects/conditions/value-control';
import { removeAt, replaceAt } from '../../projects/lib/list-utils';
import { SimpleSelect } from '../../projects/ui/simple-select';

export interface PostFunctionsEditorProps {
  value: readonly PostFunction[];
  onChange: (next: PostFunction[]) => void;
  catalog: FieldCatalog;
  projectId: string;
  idPrefix: string;
}

type PostFunctionType = PostFunction['type'];
const LABELS: Record<PostFunctionType, string> = {
  set_field: 'Set a field',
  assign: 'Assign the ticket',
  emit_event: 'Emit a custom event',
  add_comment: 'Add a comment',
};
const ASSIGN_OPTIONS = [
  { value: 'reporter', label: 'to the reporter' },
  { value: 'current_user', label: 'to the current user' },
  { value: 'unassigned', label: 'to nobody (unassign)' },
  { value: 'user', label: 'to a specific user…' },
];

const settable = (f: CatalogField) => (f.system ? SETTABLE_SYSTEM_FIELDS.has(f.key) : !f.archived);

export function newPostFunction(type: PostFunctionType, catalog: FieldCatalog): PostFunction {
  switch (type) {
    case 'set_field':
      return { type, field: catalog.fields.find(settable)?.key ?? 'assignee_id', value: null };
    case 'assign':
      return { type, to: 'current_user' };
    case 'emit_event':
      return { type, eventType: 'custom.' };
    case 'add_comment':
      return { type, body: '', visibility: 'internal' };
  }
}

/** Tokens a `set_field` value may take instead of a literal, by field type. */
function tokensFor(field: CatalogField | undefined): string[] {
  if (!field) return [];
  if (field.type === 'date' || field.type === 'datetime') return ['$now'];
  if (field.type === 'user' || field.type === 'multi_user') return ['$current_user'];
  return [];
}

/** Ordered post-functions run after the status change, inside the same transaction. */
export function PostFunctionsEditor({
  value,
  onChange,
  catalog,
  projectId,
  idPrefix,
}: PostFunctionsEditorProps) {
  const loadUsers = useUserOptionLoader();
  const canned = useCannedResponses({ projectId, pageSize: 200 });
  const update = (index: number, next: PostFunction) => onChange(replaceAt(value, index, next));

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-fg-muted text-sm">No post-functions: only the status changes.</p>
      ) : (
        <ol className="space-y-2">
          {value.map((pf, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; their position is the key
              key={`${idPrefix}-${index}-${pf.type}`}
              className="space-y-2 rounded-md border border-border bg-surface-raised p-3"
            >
              <div className="flex items-center gap-2">
                <span className="text-fg-muted text-xs tabular-nums">{index + 1}.</span>
                <Badge variant="secondary">{LABELS[pf.type]}</Badge>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-auto"
                  aria-label={`Remove post-function ${index + 1}`}
                  onClick={() => onChange(removeAt(value, index))}
                >
                  <Trash2 />
                </Button>
              </div>
              {pf.type === 'set_field' ? (
                <div className="flex flex-wrap items-start gap-2">
                  <SimpleSelect
                    aria-label="Field to set"
                    size="sm"
                    className="w-56"
                    value={pf.field}
                    options={fieldOptions(catalog, settable)}
                    onChange={(next) =>
                      next && update(index, { type: 'set_field', field: next, value: null })
                    }
                  />
                  <div className="min-w-56 flex-1">
                    <ConditionValueControl
                      id={`${idPrefix}-${index}-value`}
                      field={catalog.byKey.get(pf.field)}
                      op="eq"
                      value={pf.value === null ? undefined : pf.value}
                      allowMe={false}
                      tokens={tokensFor(catalog.byKey.get(pf.field))}
                      onChange={(next) =>
                        update(index, { ...pf, value: next === undefined ? null : next })
                      }
                    />
                  </div>
                </div>
              ) : null}
              {pf.type === 'assign' ? (
                <div className="flex flex-wrap items-center gap-2">
                  <SimpleSelect
                    aria-label="Assign to"
                    size="sm"
                    className="w-56"
                    value={typeof pf.to === 'string' ? pf.to : 'user'}
                    options={ASSIGN_OPTIONS}
                    onChange={(next) => {
                      if (!next) return;
                      update(index, {
                        type: 'assign',
                        to:
                          next === 'user'
                            ? { userId: '' }
                            : (next as 'reporter' | 'current_user' | 'unassigned'),
                      });
                    }}
                  />
                  {typeof pf.to === 'object' ? (
                    <div className="min-w-56 flex-1">
                      <AsyncPicker
                        id={`${idPrefix}-${index}-user`}
                        value={pf.to.userId || undefined}
                        onChange={(next) =>
                          update(index, { type: 'assign', to: { userId: next ?? '' } })
                        }
                        load={loadUsers}
                        placeholder="Choose a user…"
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
              {pf.type === 'emit_event' ? (
                <Input
                  aria-label="Event type"
                  value={pf.eventType}
                  maxLength={80}
                  className="h-8 w-72 font-mono text-xs"
                  placeholder="custom.something_happened"
                  onChange={(e) => update(index, { type: 'emit_event', eventType: e.target.value })}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== '' && !v.startsWith('custom.'))
                      update(index, { type: 'emit_event', eventType: `custom.${v}` });
                  }}
                  aria-invalid={
                    pf.eventType !== '' && !pf.eventType.startsWith('custom.') ? true : undefined
                  }
                />
              ) : null}
              {pf.type === 'add_comment' ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <SimpleSelect
                      aria-label="Comment visibility"
                      size="sm"
                      className="w-40"
                      value={pf.visibility}
                      options={[
                        { value: 'internal', label: 'Internal note' },
                        { value: 'public', label: 'Public reply' },
                      ]}
                      onChange={(next) =>
                        next && update(index, { ...pf, visibility: next as 'public' | 'internal' })
                      }
                    />
                    {canned.items.length > 0 ? (
                      <SimpleSelect
                        aria-label="Insert canned response"
                        size="sm"
                        className="w-64"
                        value={undefined}
                        placeholder="Insert canned response…"
                        options={canned.items.map((c) => ({ value: c.id, label: c.name }))}
                        onChange={(next) => {
                          const c = canned.items.find((x) => x.id === next);
                          if (c) update(index, { ...pf, body: c.body, visibility: c.visibility });
                        }}
                      />
                    ) : null}
                  </div>
                  <Textarea
                    aria-label="Comment body"
                    value={pf.body}
                    maxLength={5000}
                    rows={3}
                    placeholder="Resolved by {{currentUser.displayName}}."
                    onChange={(e) => update(index, { ...pf, body: e.target.value })}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={value.length >= 20}>
            <Plus />
            Add post-function
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {(Object.keys(LABELS) as PostFunctionType[]).map((type) => (
            <DropdownMenuItem
              key={type}
              onSelect={() => onChange([...value, newPostFunction(type, catalog)])}
            >
              {LABELS[type]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
